/**
 * AdminQrLogin — 微信扫码登录的状态机 + 二维码面板 (单一真相, 登录墙与"更换账号"共用)。
 *
 * 设计依据: 设计稿 frag-adm-e #ae-login (批注 ①②③④)。
 *
 * 为什么抽出来: 断开态现在走整屏登录墙 (AdminLoginWall), 已登录后"更换账号"仍走 Modal
 * (AccountConnect)。两处要的是同一张码、同一套状态机、同一份倒计时与上下文卡 —— 只抽一次,
 * 免得两边各写一遍再各错一遍。
 *
 * 四态 (稿批注④): 等待=info / 成功=success / 拒绝=danger / 过期=muted。
 * 失效态 (过期/拒绝/异常) 一律在码上打蒙层 + 原位续码, 不再让死码裸露为"可扫"。
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { App, Button, Input, Typography } from 'antd';

import { HearthTag } from '../components/HearthTag';
import { AdminAuthService, OpenAPI } from '../../.generated/api-client';
import { apiErrorMessage, setAdminToken } from '../services/api';
import {
  buildAbsoluteApiUrl,
  buildLoginContextRows,
  countdownPercent,
  deviceLabel,
  formatCountdown,
  loginActorLine,
  mapExchange,
  mapTicket,
  qrDeadVeilText,
  qrIsDead,
  qrStatusColor,
  qrStatusText,
  ticketRemainingSeconds,
  type AdminLoginTicket,
  type QrLoginState,
} from './accountConnectModel';

/** 成功态驻留 (稿态卡③: "1 秒后进入经营总览", 短暂驻留即跳转, 不留死屏)。 */
const SUCCESS_DWELL_MS = 900;

export interface QrLoginController {
  ticket: AdminLoginTicket | null;
  state: QrLoginState;
  error: string | null;
  wxacodeUrl: string;
  /** null = 后端没给 TTL → 不显倒计时 (也不据此判过期)。 */
  remainSeconds: number | null;
  remainPercent: number;
  restart: () => void;
  /** 本地开发 / e2e 的临时凭证直连 (生产构建里入口根本不渲染, 见 credentialFallbackEnabled)。 */
  applyCredential: (token: string) => void;
}

/** 临时访问凭证兜底: 仅 DEV 构建或显式开关 (e2e) 时存在, 生产不渲染。 */
export const credentialFallbackEnabled =
  import.meta.env.DEV || import.meta.env.VITE_ENABLE_ADMIN_TOKEN_FALLBACK === 'true';

/**
 * 扫码登录状态机。
 *
 * 倒计时 (稿批注③): 以票据 expires_at 绝对时刻为锚, 本地每秒推算 —— 旧实现直接显轮询回包的
 * expires_in_seconds, 两次轮询 (2s) 之间数字是冻结的, 看着像卡死。轮询回包只做校正。
 */
export function useQrLogin({
  enabled,
  onAuthenticated,
}: {
  enabled: boolean;
  onAuthenticated: () => void;
}): QrLoginController {
  const [ticket, setTicket] = useState<AdminLoginTicket | null>(null);
  const [state, setState] = useState<QrLoginState>('idle');
  const [error, setError] = useState<string | null>(null);
  const [nowMs, setNowMs] = useState<number>(() => Date.now());
  // 票据回包到达本地的时刻 —— 倒计时的锚 (见 ticketRemainingSeconds: 锚相对 TTL 而不是
  // 绝对 expires_at, 否则用户电脑时钟一偏, 新码一出生就被判过期, 直接登不进来)。
  const [receivedAtMs, setReceivedAtMs] = useState<number>(() => Date.now());
  const pollTimerRef = useRef<number | null>(null);
  const dwellTimerRef = useRef<number | null>(null);
  const authedRef = useRef(onAuthenticated);
  authedRef.current = onAuthenticated;

  const clearTimers = useCallback(() => {
    if (pollTimerRef.current !== null) {
      window.clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    if (dwellTimerRef.current !== null) {
      window.clearTimeout(dwellTimerRef.current);
      dwellTimerRef.current = null;
    }
  }, []);

  const pollTicket = useCallback(
    (ticketId: string, delayMs: number) => {
      if (pollTimerRef.current !== null) {
        window.clearTimeout(pollTimerRef.current);
      }
      pollTimerRef.current = window.setTimeout(() => {
        AdminAuthService.loginTicketStatusEndpointApiV1AdminAuthLoginTicketsTicketGet({
          ticket: ticketId,
        })
          .then(async (response) => {
            const next = mapTicket(response);
            setTicket(next);
            // 每次轮询回包都重置锚点 → 本地倒计时被服务端持续校正。
            setReceivedAtMs(Date.now());
            setNowMs(Date.now());

            if (next.status === 'confirmed') {
              setState('exchanging');
              const exchanged = mapExchange(
                await AdminAuthService.exchangeLoginTicketEndpointApiV1AdminAuthLoginTicketsTicketExchangePost(
                  { ticket: ticketId },
                ),
              );
              // 先落 token (拿到就存, 不冒"驻留期间丢凭证"的险), 再让成功态驻留 ~1s 给用户
              // 看清"登进来的是谁", 到点才通知外层切壳。
              setAdminToken(exchanged.jwt);
              setState('success');
              dwellTimerRef.current = window.setTimeout(() => {
                authedRef.current();
              }, SUCCESS_DWELL_MS);
              return;
            }

            if (next.status === 'expired' || next.status === 'consumed') {
              setState('expired');
              setError(null);
              return;
            }

            if (next.status === 'denied') {
              setState('denied');
              setError(null);
              return;
            }

            setState('waiting');
            pollTicket(ticketId, Math.max(1, next.polling_interval_seconds) * 1000);
          })
          .catch((err: unknown) => {
            setState('error');
            setError(apiErrorMessage(err));
          });
      }, delayMs);
    },
    [],
  );

  const restart = useCallback(() => {
    clearTimers();
    setState('creating');
    setError(null);
    setTicket(null);
    AdminAuthService.createLoginTicketEndpointApiV1AdminAuthLoginTicketsPost({
      requestBody: { device_label: deviceLabel(navigator.platform) },
    })
      .then((response) => {
        const next = mapTicket(response);
        setTicket(next);
        setReceivedAtMs(Date.now());
        setNowMs(Date.now());
        setState('waiting');
        pollTicket(next.ticket, Math.max(1, next.polling_interval_seconds) * 1000);
      })
      .catch((err: unknown) => {
        setState('error');
        setError(apiErrorMessage(err));
      });
  }, [clearTimers, pollTicket]);

  useEffect(() => {
    if (!enabled) {
      clearTimers();
      setState('idle');
      setTicket(null);
      setError(null);
      return;
    }
    restart();
    return clearTimers;
  }, [clearTimers, enabled, restart]);

  useEffect(() => clearTimers, [clearTimers]);

  // 每秒推进本地时钟 (仅等待态需要): 真倒计时 + 烧退进度条。
  useEffect(() => {
    if (state !== 'waiting') return;
    const id = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [state]);

  const remainSeconds = ticketRemainingSeconds(ticket, receivedAtMs, nowMs);

  // 本地倒计时烧完即判过期 —— 不必等下一次轮询回包才把死码标出来。
  // remainSeconds === null (后端没给 TTL) 时**不判过期**: 缺字段不等于已过期。
  useEffect(() => {
    if (state === 'waiting' && ticket && remainSeconds !== null && remainSeconds <= 0) {
      clearTimers();
      setState('expired');
    }
  }, [clearTimers, remainSeconds, state, ticket]);

  const applyCredential = useCallback(
    (token: string) => {
      clearTimers();
      setAdminToken(token);
      authedRef.current();
    },
    [clearTimers],
  );

  return {
    ticket,
    state,
    error,
    wxacodeUrl: ticket ? buildAbsoluteApiUrl(ticket.wxacode_url, OpenAPI.BASE) : '',
    remainSeconds,
    remainPercent: countdownPercent(remainSeconds ?? 0, ticket?.expires_in_seconds ?? 0),
    restart,
    applyCredential,
  };
}

/**
 * 临时访问凭证入口 (仅本地开发 / e2e)。
 *
 * 断开态改走登录墙后, 这个入口必须跟到墙上 —— 否则本地开发与 e2e 在没有真微信扫码的
 * 情况下**无法登录**。生产构建 credentialFallbackEnabled 为 false, 整块不渲染。
 */
function DevCredentialFallback({ onApply }: { onApply: (token: string) => void }) {
  const { message } = App.useApp();
  const [open, setOpen] = useState(false);
  const [credential, setCredential] = useState('');

  if (!credentialFallbackEnabled) {
    return null;
  }

  return (
    <div className="admin-login-dev">
      <Button type="link" className="dev-credential-toggle" onClick={() => setOpen((v) => !v)}>
        {open ? '收起临时访问凭证' : '使用临时访问凭证'}
      </Button>
      {open ? (
        <div className="admin-login-dev__form">
          <Typography.Text type="secondary">
            仅用于本地开发或迁移期排障；正式验收必须走微信扫码登录。
          </Typography.Text>
          <Input.Password
            value={credential}
            onChange={(event) => setCredential(event.target.value)}
            placeholder="粘贴管理端访问凭证"
            autoComplete="off"
          />
          <Button
            onClick={() => {
              const trimmed = credential.trim();
              if (!trimmed) {
                message.warning('请输入有效凭证');
                return;
              }
              setCredential('');
              onApply(trimmed);
            }}
          >
            保存并刷新
          </Button>
        </div>
      ) : null}
    </div>
  );
}

/**
 * 二维码面板 (登录墙 / 更换账号弹窗共用)。
 *
 * variant: wall = 登录墙里的大码 (第一印象页) / modal = "更换账号"弹窗里的小码。
 */
export function AdminQrLoginPanel({
  qr,
  variant,
}: {
  qr: QrLoginController;
  variant: 'wall' | 'modal';
}) {
  const dead = qrIsDead(qr.state);
  const contextRows = buildLoginContextRows(qr.ticket);
  const actorLine = loginActorLine(qr.ticket?.actor ?? null);

  return (
    <div className={`admin-login-panel admin-login-panel--${variant}`}>
      <div className="admin-login-head">
        <span className="admin-login-title">微信扫码登录</span>
        <span className="admin-login-sub">用已绑定 Ember 小程序的微信扫码</span>
      </div>

      <div className="admin-login-qr-wrap">
        {qr.state === 'creating' ? (
          <div className="admin-login-qr-placeholder">正在生成二维码…</div>
        ) : qr.state === 'success' ? (
          <div className="admin-login-qr-placeholder admin-login-qr-placeholder--success">
            <span className="admin-login-success-mark" aria-hidden="true" />
            <span>{actorLine || '登录成功'}</span>
          </div>
        ) : qr.ticket ? (
          <>
            <img className="admin-login-qr" src={qr.wxacodeUrl} alt="微信扫码登录 Ember 电脑端" />
            {/* 失效码打蒙层 + 原位续码 (稿批注④): 过期/拒绝后不再让一张死码裸露为"可扫"。 */}
            {dead ? (
              <button type="button" className="admin-login-qr-veil" onClick={qr.restart}>
                <span className="admin-login-qr-veil__title">{qrDeadVeilText(qr.state)}</span>
                <span className="admin-login-qr-veil__action">点击刷新</span>
              </button>
            ) : null}
          </>
        ) : (
          <div className="admin-login-qr-placeholder">二维码暂不可用</div>
        )}
      </div>

      <div className="admin-login-status">
        <HearthTag semantic={qrStatusColor(qr.state)}>{qrStatusText(qr.state)}</HearthTag>
      </div>

      {/* 真倒计时 + 烧退进度条 (与 mp 确认页同语言)。后端没给 TTL → 整块不显, 不编一个数字。 */}
      {qr.state === 'waiting' && qr.remainSeconds !== null ? (
        <div className="admin-login-countdown">
          <span className="admin-login-countdown__value">{formatCountdown(qr.remainSeconds)}</span>
          <span className="admin-login-countdown__label">后失效 · 过期可在码上点击刷新</span>
          <span className="admin-login-countdown__track">
            <span
              className="admin-login-countdown__fill"
              style={{ width: `${qr.remainPercent}%` }}
            />
          </span>
        </div>
      ) : null}

      {qr.state === 'denied' ? (
        <Typography.Text className="admin-login-note admin-login-note--danger">
          你在手机上拒绝了本次登录。若非本人操作请忽略，二维码已作废。
        </Typography.Text>
      ) : null}

      {qr.error ? (
        <Typography.Text className="admin-login-note admin-login-note--danger">
          {qr.error}
        </Typography.Text>
      ) : null}

      {/* 「本次请求」上下文 (稿批注②): 与手机确认页显示的三项同源同值, 两屏一致才确认。
          后端已定稿不下发短确认码 (admin_auth/schemas.py), 故此处不编码、不占位。 */}
      {qr.state !== 'success' ? (
        <div className="admin-login-context">
          <span className="admin-login-context__key">本次登录发起自</span>
          {contextRows.map((row) => (
            <div className="admin-login-context__row" key={row.key}>
              <span className="admin-login-context__label">{row.label}</span>
              <span
                className={`admin-login-context__value${
                  row.unknown ? ' admin-login-context__value--unknown' : ''
                }`}
              >
                {row.value}
              </span>
            </div>
          ))}
        </div>
      ) : null}

      <Typography.Text className="admin-login-hint">
        {qr.state === 'success'
          ? '正在进入管理后台…'
          : '请核对手机确认页上的设备、来源 IP 与发起时间，与此处一致后再点确认。电脑端不保存微信信息。'}
      </Typography.Text>

      {qr.state === 'success' ? null : <DevCredentialFallback onApply={qr.applyCredential} />}
    </div>
  );
}

export default AdminQrLoginPanel;
